import React, {useEffect, useState} from 'react';
import {Box, Button, Checkbox, Container, FormControlLabel, Link, Paper, Stack, Typography} from '@mui/material';
import api from '../../axios';
import {useLocation, useNavigate} from 'react-router-dom';
import {validateFieldLength} from '../../utility/validationUtil';
import AdmicityForm from '../../shared-components/AdmicityForm';
import {getRouteForUser} from '../../constants/roles';
import useUser from '../../utility/hooks/useUser';
import {getSubdomain, isLocalhostHost} from '../../utility/routeUtil';
import {useGetSchoolInvitesInfoQuery} from '../../api/services/schoolManagementService';
import {useDispatch} from 'react-redux';
import {login, logout, resendMfa, verifyMfa} from '../../AppLayout/ApplicationSlice';

const mfaFields = [
    {
        label: 'Verification code',
        name: 'code',
        type: 'text',
        validate: (value) => {
            const isValid = /^\d{6}$/.test(value || '');
            return {
                isValid,
                message: isValid ? '' : 'Enter the 6-digit code from your email'
            };
        }
    }
];

const toMfaChallenge = (result) => ({
    challengeId: result.challengeId,
    maskedEmail: result.maskedEmail,
    deviceTrustEnabled: result.deviceTrustEnabled !== false,
    expiresAt: result.expiresAt
});

const LoginPage = () => {
    const [loginError, setLoginError] = useState('');
    const [mfaChallenge, setMfaChallenge] = useState(null);
    const [trustDevice, setTrustDevice] = useState(true);
    const [codeExpired, setCodeExpired] = useState(false);
    const [isResending, setIsResending] = useState(false);
    const navigate = useNavigate();
    const {state: locationState} = useLocation();
    const dispatch = useDispatch();
    const {user, account} = useUser();

    const {
        data: invitesInfo = {
            disableStudentInvitations: true,
            disableParentInvitations: true
        },
    } = useGetSchoolInvitesInfoQuery(getSubdomain(),
        {skip: !getSubdomain()}
    );

    useEffect(() => {
        if (user.id && account.isAuthenticated) {
            api.post('/account/session/validate')
                .then(response => {
                    if (response.data) {
                        const path = locationState?.redirectTo
                            ? locationState?.redirectTo
                            : getRouteForUser(user);

                        if (user.domain != null && !isLocalhostHost()) {
                            window.location.href = `https://${user.domain}${path}`;
                        } else {
                            navigate(path, {replace: true});
                        }
                    }
                })
                .catch(reason => {
                    console.error(reason);
                    dispatch(logout());
                });
        }
    }, [user.id, account.isAuthenticated]);

    useEffect(() => {
        if (!mfaChallenge?.expiresAt) {
            return;
        }

        setCodeExpired(false);
        const remainingMs = new Date(mfaChallenge.expiresAt).getTime() - Date.now();
        if (remainingMs <= 0) {
            setCodeExpired(true);
            return;
        }

        const timeoutId = setTimeout(() => setCodeExpired(true), remainingMs);
        return () => clearTimeout(timeoutId);
    }, [mfaChallenge]);

    const handleLogin = async (credentials) => {
        try {
            const result = await dispatch(login(credentials)).unwrap();
            if (result?.mfaRequired) {
                const challenge = toMfaChallenge(result);
                setMfaChallenge(challenge);
                setTrustDevice(challenge.deviceTrustEnabled);
                setCodeExpired(false);
                setLoginError('');
            }
        } catch (e) {
            if (e?.message === 'Request failed with status code 401') {
                setLoginError('Incorrect username or password.');
            } else {
                setLoginError('Something went wrong. Please try again later.');
            }
        }
    };

    const handleVerifyMfa = async ({code}) => {
        try {
            await dispatch(verifyMfa({
                challengeId: mfaChallenge.challengeId,
                code,
                trustDevice: Boolean(mfaChallenge.deviceTrustEnabled) && trustDevice
            })).unwrap();
        } catch (e) {
            if (e?.message === 'Request failed with status code 401') {
                setLoginError('Invalid or expired verification code.');
                const expiresAtMs = mfaChallenge.expiresAt
                    ? new Date(mfaChallenge.expiresAt).getTime()
                    : 0;
                if (!mfaChallenge.expiresAt || expiresAtMs <= Date.now()) {
                    setCodeExpired(true);
                }
            } else {
                setLoginError('Something went wrong. Please try again later.');
            }
        }
    };

    const handleResendCode = async () => {
        try {
            setIsResending(true);
            const result = await dispatch(resendMfa({
                challengeId: mfaChallenge.challengeId
            })).unwrap();
            setMfaChallenge(toMfaChallenge(result));
            setCodeExpired(false);
            setLoginError('');
        } catch {
            setLoginError('Unable to resend the verification code. Please try again.');
            setCodeExpired(true);
        } finally {
            setIsResending(false);
        }
    };

    const textFields = [
        {
            label: 'Email',
            name: 'email',
            type: 'email',
            validate: (value) => validateFieldLength(value, 320)
        },
        {
            label: 'Password',
            type: 'password',
            name: 'password',
        }
    ];

    const getRegionSwitchInfo = () => {
        const host = window.location.hostname;
        const isEU = host.includes('eu.');

        return {
            isEU,
            switchUrl: isEU
                ? 'https://start-with.admicity.co.uk/login'
                : 'https://start-with.eu.admicity.co.uk/login',
            currentRegion: isEU ? 'EU' : 'UK',
            otherRegion: isEU ? 'UK' : 'EU',
        };
    };

    const { switchUrl, currentRegion, otherRegion } = getRegionSwitchInfo();

    return (
        <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            minHeight="100%"
            flexGrow={1}
        >
            <Container maxWidth="xs">
                <Paper
                    variant="outlined"
                    sx={{p: 3, display: 'flex', flexDirection: 'column'}}
                    onClick={() => setLoginError('')}
                >
                    {!mfaChallenge
                        ? (
                            <>
                                <AdmicityForm
                                    title="Log in"
                                    textFields={textFields}
                                    handleSubmit={handleLogin}
                                    handleChange={() => setLoginError('')}
                                    buttonText="Log In"
                                    errorCaption={loginError}
                                />
                                <Stack display={'flex'} flexDirection={'row'} justifyContent={'space-between'}>
                                    <Button variant={'text'}
                                            sx={{textTransform: 'none'}}
                                            onClick={() => {
                                                navigate('/password-recovery');
                                            }}>
                                        Forgot password?
                                    </Button>
                                    {(!invitesInfo.disableParentInvitations || !invitesInfo.disableStudentInvitations || !invitesInfo.disableApplicationParentInvitations) &&
                                        <Button variant={'text'}
                                                sx={{textTransform: 'none'}}
                                                onClick={() => {
                                                    navigate('/invite-recovery');
                                                }}>
                                            Resend registration link
                                        </Button>}
                                </Stack>
                            </>
                        )
                        : (
                            <>
                                <AdmicityForm
                                    key={mfaChallenge.challengeId}
                                    title="Verify your identity"
                                    textFields={mfaFields}
                                    handleSubmit={handleVerifyMfa}
                                    handleChange={() => setLoginError('')}
                                    buttonText="Verify"
                                    errorCaption={loginError}
                                    isButtonDisabled={codeExpired}
                                />
                                <Typography variant="body2" color="text.secondary" sx={{mt: 1}}>
                                    We sent a verification code to {mfaChallenge.maskedEmail}.
                                </Typography>
                                {mfaChallenge.deviceTrustEnabled && (
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                checked={trustDevice}
                                                onChange={(event) => setTrustDevice(event.target.checked)}
                                            />
                                        }
                                        label="Trust this device"
                                        sx={{mt: 1}}
                                    />
                                )}
                                {codeExpired && (
                                    <>
                                        <Typography variant="body2" color="text.secondary" sx={{mt: 1}}>
                                            Your verification code has expired. Request a new code to continue.
                                        </Typography>
                                        <Button
                                            variant="text"
                                            sx={{textTransform: 'none', alignSelf: 'flex-start'}}
                                            disabled={isResending}
                                            onClick={handleResendCode}
                                        >
                                            Resend code
                                        </Button>
                                    </>
                                )}
                                <Button
                                    variant="text"
                                    sx={{textTransform: 'none', alignSelf: 'flex-start'}}
                                    onClick={() => {
                                        setMfaChallenge(null);
                                        setCodeExpired(false);
                                        setLoginError('');
                                    }}
                                >
                                    Back to login
                                </Button>
                            </>
                        )}
                    <Box mt={1} textAlign="center">
                        <Typography variant="body2">
                            {currentRegion} | <Link href={switchUrl}>{otherRegion}</Link>
                        </Typography>
                    </Box>
                </Paper>
            </Container>
        </Box>
    );
};

export default LoginPage;
